import React, { ComponentProps, MouseEventHandler, ReactNode, useContext } from 'react';
import getIn from '../../../components/connectedForm/utils/getIn';
import HeaderContainer from '../HeaderContainer';
import HeaderTitle from '../HeaderTitle';
import HeaderAction from '../../../components/connectedForm/HeaderAction';
import ActionButtons from '../../../components/connectedForm/ActionButtons';
import { DangerButton, SecondaryButton, WarningButton } from '../Button';
import BreadCrumbs from '../../../components/connectedForm/BreadCrumbs';
import ConnectedFormContext from '../../Context/ConnectedFormContext';
import { SaveSelectButton } from '../../SelectButton';
import { getStatusIndicatorFromValues } from './HeaderStatusUtils';
import { IAlert } from '../../../graphql/schema/p4types';
import { HeaderNotification } from '../../Notification/HeaderNotification';
import { StatusStyle } from '../HeaderStatus';
import { useHistory } from 'react-router';
import { IDeprecatedFormAction, IFormAction } from '../../../@types/entityTypes';

export type IStatusFlagStatus = {
  value: any;
  style: StatusStyle;
  title?: string;
  icon?: string;
  tooltip?: string;
} & ({ title: string } | { titleKey: string });

export type IStatusFlag = {
  key: string;
  status: Array<IStatusFlagStatus>;
  override?: boolean; //Used to display a status flag that's not based on values.
};

export type IHeaderBlockProps = {
  name: string;

  onSubmit: MouseEventHandler; //Save function provided by ConnectedForm.
  onCancel: MouseEventHandler;
  onSave?: MouseEventHandler; //Override save function for specific forms (E.g. Manual Dispatch).
  onDelete: MouseEventHandler;
  onRestore: MouseEventHandler;

  saveOptions?: any;
  isModal?: boolean;
  deletePermitted?: boolean;
  flags?: Array<any>;
  extraBtns?: Array<JSX.Element>;
  cancelText: string;
  entityHdr: string;
  entityHdr2: string | null;
  entityHdr3: string | null;
  noDeleteOption?: boolean;
  allowRestoreOption?: boolean;
  values: any | Array<any>;
  includeBorder: boolean;

  actionButtons?: Array<IFormAction<any>>;
  entityHdrStyle?: 'small';
  entityHdrValue: any;
  statusFlags?: Array<IStatusFlag>;
  noSaveOption?: boolean;
  readOnly?: boolean;
  subtitle?: string;
  archive?: any;
  entity?: any;
  customAlertHandler?: Function;
  saveReadOnlyCondition?: (defaultReadOnly?: boolean) => boolean;

  detailView?: any;
  customBreadcrumb?: any;

  history?: any;
  beta?: boolean;
  hint?: string;
  renderCancelButton?: any;
  saveButtonProps?: ComponentProps<typeof SaveSelectButton>; //Allow pass-through of arbitrary props to save button, such as 'disabled'
};

export function isNotArchived(entity: any) {
  return !entity?.archived;
}

const statusStyleToOrder = (statusStyle: StatusStyle): number => {
  switch (statusStyle) {
    case StatusStyle.Info:
      return 0;
    case StatusStyle.Danger:
      return 1;
    case StatusStyle.Warning:
      return 2;
    case StatusStyle.Ready:
      return 3;
    case StatusStyle.Success:
      return 4;
    case StatusStyle.Basic:
      return 5;
    default:
      return 6;
  }
};

export const HeaderStatusFlags = ({
  statusFlags,
  values,
}: {
  statusFlags: IStatusFlag | Array<IStatusFlag>;
  values: any;
}) => {
  const statusIndicators: Array<ReactNode> = [];
  if (statusFlags && Array.isArray(statusFlags)) {
    for (let i = 0; i < statusFlags.length; i++) {
      statusIndicators.push(...getStatusIndicatorFromValues(statusFlags[i], values));
    }
  } else if (statusFlags) {
    statusIndicators.push(...getStatusIndicatorFromValues(statusFlags, values));
  }

  const isFullyAllocated = statusIndicators.some(
    (statusElement) => (statusElement as any)?.props?.title === 'Fully Allocated'
  );

  return (
    <>
      {statusIndicators
        .filter(
          (statusElement) =>
            !!statusElement && !(isFullyAllocated && (statusElement as any)?.props?.title === 'Allocated')
        )
        .sort((a: any, b: any) => {
          const aOrder = statusStyleToOrder(a?.props?.statusStyle);
          const bOrder = statusStyleToOrder(b?.props?.statusStyle);
          return aOrder - bOrder;
        })}
    </>
  );
};

// eslint-disable-next-line complexity
const HeaderBlock = (props: IHeaderBlockProps) => {
  const connectedForm = useContext(ConnectedFormContext);
  const history = useHistory<{ cancelButtonDisabled?: boolean }>();

  const match: any | null = connectedForm?.match || null;
  const value: string = props.entityHdr ? getIn(props.values, props.entityHdr) : props.entityHdrValue || props.name;
  const optValue: string = props.entityHdr2 ? getIn(props.values, props.entityHdr2) : null;
  const optValue2: string = props.entityHdr3 ? getIn(props.values, props.entityHdr3) : null;

  const deletePermitted: boolean | undefined = props.deletePermitted;
  const allowRestoreOption = props.allowRestoreOption && deletePermitted;

  const deleteButton: JSX.Element | null =
    props.noDeleteOption ||
    !deletePermitted ||
    (match && match.params.id === 'New') ||
    props.values?.archived ||
    connectedForm.readOnly ? null : (
      <DangerButton data-testid="form-delete" tabIndex={2} onClick={props.onDelete as any} minWidth="80px">
        {props.archive ? 'Archive' : 'Delete'}
      </DangerButton>
    );

  const restoreButton: JSX.Element | null =
    !allowRestoreOption || (match && match.params.id === 'New') || !props.values?.archived ? null : (
      <WarningButton data-testid="form-restore" tabIndex={3} onClick={props.onRestore as any} minWidth="80px">
        Unarchive
      </WarningButton>
    );

  const onSaveClick: MouseEventHandler<any> = props.onSave || props.onSubmit;
  const onCancelClick: MouseEventHandler = props.onCancel;

  let cancelButton;
  if (history?.location?.state?.cancelButtonDisabled) {
    cancelButton = null;
  } else if (props.renderCancelButton) {
    cancelButton = props.renderCancelButton({
      'data-testid': `form-${props.cancelText}`,
      onClick: onCancelClick,
    });
  } else {
    cancelButton = (
      <SecondaryButton
        data-testid={`form-${props.cancelText}`}
        onClick={onCancelClick as any}
        tabIndex={2}
        minWidth="80px"
      >
        {props.cancelText}
      </SecondaryButton>
    );
  }

  const primaryAction: boolean = !!(
    Array.isArray(props.actionButtons) &&
    props.actionButtons.find(
      (btn) =>
        ((btn as IDeprecatedFormAction<any>).component &&
          ((btn as IDeprecatedFormAction<any>).component as any).displayName === 'Button__PrimaryButton') ||
        btn.isPrimary
    )
  );

  const saveReadOnly = props.saveReadOnlyCondition?.(props.readOnly || props.noSaveOption);

  const successButton = saveReadOnly ? null : (
    <SaveSelectButton
      primaryAction={primaryAction}
      onClick={onSaveClick}
      data-testid="form-save-primary"
      tabIndex={2}
      minWidth="80px"
      saveOptions={props.saveOptions}
      isModal={!!props.isModal}
      {...props.saveButtonProps}
    />
  );

  if (props.values?.archived && !props.statusFlags?.some((flag) => flag.key === 'archived')) {
    props.statusFlags?.push({
      key: 'archived',
      status: [
        {
          value: true,
          style: StatusStyle.Danger,
          title: 'Archived',
        },
      ],
    });
  }

  const headerAlerts: Array<IAlert> = connectedForm.headerAlerts || [];
  const headerAlertElements = headerAlerts.map((alert) => {
    return <HeaderNotification alert={alert} />;
  });

  return (
    <>
      {!props.detailView ? <BreadCrumbs customBreadcrumb={props.customBreadcrumb} title={props.name} /> : null}
      <HeaderContainer>
        <HeaderTitle
          title={props.name}
          subtitle={props.subtitle}
          value={value || ''}
          status={<HeaderStatusFlags statusFlags={props.statusFlags} values={props.values} />}
          optValue={optValue}
          optValue2={optValue2}
          entityHdrStyle={props.entityHdrStyle}
          beta={props.beta}
          hint={props.hint}
        />
        <HeaderAction>
          {cancelButton}
          {deleteButton}
          {restoreButton}
          <ActionButtons
            actionButtons={props.actionButtons ?? []}
            entity={props.entity}
            customAlertHandler={props.customAlertHandler}
          />
          {successButton}
          {props.extraBtns ? props.extraBtns : null}
        </HeaderAction>
      </HeaderContainer>
      {headerAlertElements?.length > 0 ? (
        <div
          style={{
            backgroundColor: '#faf9f8',
            paddingBottom: '4px',
            marginTop: '-20px',
          }}
        >
          {headerAlertElements}
        </div>
      ) : null}
    </>
  );
};

HeaderBlock.defaultProps = {
  cancelText: 'Cancel',
  entityHdr: '',
  entityHdr2: null,
  entityHdr3: null,
  entityHdrValue: null,
  onCancel: () => {},
  onDelete: () => {},
  onRestore: () => {},
  onSubmit: () => {},
  values: {},
  includeBorder: false,
  saveReadOnlyCondition: (ro: any) => ro,
};

export default HeaderBlock;
